import { apiService } from './api.service';
import { API_CONFIG } from '../config/api.config';
import { logger } from '../utils/logger';
import { ChartDataPoint, PieChartDataPoint } from './admin.service';
import { Session } from 'next-auth';
import { DISABLE_ALL_METRICS } from '../utils/metrics.utils';
// Flag to disable metrics API calls during development or for non-admin users
// Set to false to use real API calls
const DISABLE_METRICS_API = false;

// Configuration for batching
const BATCH_SIZE_LIMIT = 10;
const BATCH_INTERVAL_MS = 5000; // 5 seconds

interface MetricsTimeRange {
  startDate: Date;
  endDate: Date;
}

interface MetricsQuery {
  startDate?: Date;
  endDate?: Date;
  interval?: 'hour' | 'day' | 'week' | 'month';
  dimensions?: Record<string, string | number>;
  range?: 'all' | 'year' | 'month' | 'week' | 'day';
}

interface MetricsResponse {
  data: Array<{
    timestamp: string;
    name: string;
    value: number;
    dimensions?: Record<string, string | number>;
  }>;
}

/**
 * FeedbackMetrics interface updated to allow nulls for number fields
 */
interface FeedbackMetrics {
  positive: number | null;
  negative: number | null;
  total: number | null;
  feedbackRate: number | null;
  topReasons: Array<{ name: string; count: number }>;
}

/**
 * TopicMetrics interface updated to allow nulls for number fields
 */
interface TopicMetrics {
  topics: Array<{ name: string; count: number }>;
  totalQuestions: number | null;
  averageQuestionsPerUser: number | null;
}

// Update the InteractionMetrics interface to match the API response
interface InteractionMetrics {
  totalInteractions: number;
  averageInteractionsPerUser: number;
  interactionsByType: Array<{ name: string; count: number }>;
}

// Original interface for backward compatibility if needed
interface LegacyInteractionMetrics {
  suggestedQuestionClicks: number;
  legalSourceClicks: number;
  ccqArticleClicks: number;
  clickThroughRate: number;
}

interface TimeDistributionMetrics {
  hourly: ChartDataPoint[];
  daily: ChartDataPoint[];
  weekly: ChartDataPoint[];
  monthly: ChartDataPoint[];
}

/**
 * AllMetrics interface updated for new backend structure
 */
interface AllMetrics {
  userActivity?: {
    dau: ChartDataPoint[];
    mau: ChartDataPoint[];
    averageSessionDuration: number | null;
    legacyActivity?: ChartDataPoint[];
  };
  timeDistribution?: TimeDistributionMetrics;
  retention?: ChartDataPoint[];
  topics?: TopicMetrics;
  feedback?: FeedbackMetrics;
  interactions?: InteractionMetrics;
  deviceUsage?: PieChartDataPoint[];
  browserDistribution?: PieChartDataPoint[];
  responseTimes?: {
    averageLambdaResponseTime: number | null;
    medianLambdaResponseTime: number | null;
    p95LambdaResponseTime: number | null;
    averageFrontendResponseTime: number | null;
    medianFrontendResponseTime: number | null;
    p95FrontendResponseTime: number | null;
  };
  conversationLength?: {
    averageLength: number | null;
    distribution: ChartDataPoint[];
    totalMessages: number | null;
    averageDuration?: number | null;
  };
  sourceClicks?: {
    totalSources: number | null;
    clickedSources: number | null;
    clickedPercentage: number | null;
    perType: Array<{
      type: string;
      total: number;
      clicked: number;
      percentage: number; // clicks / total
      percentageClickedSources: number; // clicked sources / total
    }>;
    avgClicksPerConversation: number | null;
  };
  pageViews: {
    total: number | null;
    perPage: Array<{ page: string | null; count: number | null }>;
    perUser: Array<{ userId: string | null; count: number | null }>;
    perGroup: Array<{ group: string | null; count: number | null }>;
    perSession: Array<{ sessionId: string | null; count: number | null }>;
    timeSeries: Array<{ date: Date | null; count: number | null }>;
  };
  followUps?: {
    totalGenerated: number | null;
    totalClicked: number | null;
    clickRate: number | null;
    perCategory: Array<{
      category: string;
      generated: number;
      clicked: number;
      rate: number;
    }>;
    perMethod: Array<{
      method: string;
      generated: number;
      clicked: number;
      rate: number;
    }>;
    timeSeries: Array<{
      date: string;
      generated: number;
      clicked: number;
    }>;
    followUpsList: Array<{
      adviceItemId: string;
      originalQuestion: string;
      createdAt: string;
      followUps: Array<{
        id: string;
        question: string;
        category: string;
        order: number;
        isClicked: boolean;
        clickedAt: string | null;
        generationMethod: string;
        relevanceScore: number;
      }>;
    }>;
    topPerformers: Array<{
      id: string;
      question: string;
      category: string;
      generationMethod: string;
      relevanceScore: number;
      clickedAt: string | null;
    }>;
    avgRelevanceScore: number | null;
    aiGenerated: number | null;
    ruleBased: number | null;
  };
}

// Interface for the event data tracked
interface TrackedEventData {
  category: string;
  action: string;
  label?: string;
  value?: number;
  userId?: string;
  sessionId?: string;
  dimensions?: Record<string, any>;
  timestamp: string; // Added timestamp
  [key: string]: any;
}

class MetricsService {
  // Cache for metrics data
  private metricsCache: {
    data: AllMetrics | null;
    timestamp: number;
    ttl: number; // Time to live in milliseconds
    cacheKey: string | null; // Include cache key based on query params
  } = {
    data: null,
    timestamp: 0,
    ttl: 5 * 60 * 1000, // 5 minutes cache
    cacheKey: null,
  };

  // Batching state
  private eventQueue: TrackedEventData[] = [];
  private batchTimerId: NodeJS.Timeout | null = null;
  private currentSession: Session | null = null; // Store session for batch sending

  constructor() {
    // Ensure batch is sent on page unload
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => this._sendBatch());
    }
  }

  /**
   * Update the session used for sending batched events.
   */
  public setSession(session: Session | null) {
    this.currentSession = session;
    // If session becomes invalid while queue has items, try sending immediately
    if (!session && this.eventQueue.length > 0) {
      this._sendBatch();
    }
  }

  // Consolidated method to fetch all metrics at once
  async getAllMetrics(
    query: MetricsTimeRange,
    session?: Session | null
  ): Promise<AllMetrics> {
    // Check if user is admin
    const isAdmin = session?.user?.isAdmin === true;

    // For non-admin users, return empty data
    if (!isAdmin) {
      logger.debug('User is not an admin, cannot access metrics');
      throw new Error('Unauthorized: Admin access required');
    }

    // Ensure we're using valid dates (not in the future)
    const currentDate = new Date();
    // Create copies of dates to avoid mutating the original objects from the store
    const startDate = query.startDate ? new Date(query.startDate) : new Date(currentDate);
    if (!query.startDate) {
      startDate.setDate(startDate.getDate() - 30); // 30 days ago
    }
    const endDate = query.endDate ? new Date(query.endDate) : new Date(currentDate);

    // Set time to start of day for startDate and end of day for endDate
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);

    // Create cache key based on date range
    const cacheKey = `${startDate.toISOString()}-${endDate.toISOString()}`;

    // Check if we have valid cached data for this date range
    const now = Date.now();
    if (
      this.metricsCache.data &&
      this.metricsCache.cacheKey === cacheKey &&
      now - this.metricsCache.timestamp < this.metricsCache.ttl
    ) {
      logger.debug('Using cached metrics data for date range:', { cacheKey });
      return this.metricsCache.data;
    }

    try {

      // Create URLSearchParams object for proper parameter encoding
      const searchParams = new URLSearchParams();
      searchParams.append('startDate', startDate.toISOString());
      searchParams.append('endDate', endDate.toISOString());

      logger.debug('Fetching metrics with params:', {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      });

      // Make a single API call to fetch all metrics
      const response = await apiService.get<AllMetrics>(
        `${API_CONFIG.METRICS.ALL}?${searchParams.toString()}`,
        session
      );

      logger.info('[getAllMetrics] API response received:', {
        hasResponse: !!response,
        hasFollowUps: !!response?.followUps,
        followUpsTotal: response?.followUps?.totalGenerated,
        followUpsKeys: response?.followUps ? Object.keys(response.followUps) : [],
      });

      if (!response) {
        logger.warn('No response from metrics API');
        return {
          userActivity: undefined,
          timeDistribution: {
            hourly: [],
            daily: [],
            weekly: [],
            monthly: [],
          },
          retention: [],
          topics: {
            topics: [],
            totalQuestions: null,
            averageQuestionsPerUser: null,
          },
          feedback: {
            positive: null,
            negative: null,
            total: null,
            feedbackRate: null,
            topReasons: [],
          },
          interactions: {
            totalInteractions: 0,
            averageInteractionsPerUser: 0,
            interactionsByType: [],
          },
          responseTimes: {
            averageLambdaResponseTime: null,
            medianLambdaResponseTime: null,
            p95LambdaResponseTime: null,
            averageFrontendResponseTime: null,
            medianFrontendResponseTime: null,
            p95FrontendResponseTime: null,
          },
          conversationLength: {
            averageLength: null,
            distribution: [],
            totalMessages: null,
            averageDuration: null,
          },
          pageViews: {
            total: 0,
            perPage: [{ page: null, count: 0 }],
            perSession: [{ sessionId: null, count: 0 }],
            perUser: [{ userId: null, count: 0 }],
            perGroup: [{ group: null, count: 0 }],
            timeSeries: [{ date: null, count: 0 }],
          },
          followUps: undefined,
        };
      }

      // Transform the response data for new backend structure
      const transformedResponse: AllMetrics = {
        userActivity: response.userActivity
          ? {
              dau: response.userActivity.dau || [],
              mau: response.userActivity.mau || [],
              averageSessionDuration:
                response.userActivity.averageSessionDuration ?? null,
              legacyActivity: response.userActivity.legacyActivity || [],
            }
          : undefined,
        timeDistribution: {
          hourly: response.timeDistribution?.hourly || [],
          daily: response.timeDistribution?.daily || [],
          weekly: response.timeDistribution?.weekly || [],
          monthly: response.timeDistribution?.monthly || [],
        },
        retention: Array.isArray(response.retention) ? response.retention : [],
        topics: response.topics || {
          topics: [],
          totalQuestions: null,
          averageQuestionsPerUser: null,
        },
        feedback: response.feedback || {
          positive: null,
          negative: null,
          total: null,
          feedbackRate: null,
          topReasons: [],
        },
        interactions: response.interactions || {
          totalInteractions: 0,
          averageInteractionsPerUser: 0,
          interactionsByType: [],
        },
        deviceUsage: Array.isArray(response.deviceUsage)
          ? response.deviceUsage
          : [],
        browserDistribution: Array.isArray(response.browserDistribution)
          ? response.browserDistribution
          : [],
        responseTimes: response.responseTimes
          ? {
              averageLambdaResponseTime:
                response.responseTimes.averageLambdaResponseTime ?? null,
              medianLambdaResponseTime:
                response.responseTimes.medianLambdaResponseTime ?? null,
              p95LambdaResponseTime:
                response.responseTimes.p95LambdaResponseTime ?? null,
              averageFrontendResponseTime:
                response.responseTimes.averageFrontendResponseTime ?? null,
              medianFrontendResponseTime:
                response.responseTimes.medianFrontendResponseTime ?? null,
              p95FrontendResponseTime:
                response.responseTimes.p95FrontendResponseTime ?? null,
            }
          : undefined,
        conversationLength: response.conversationLength
          ? {
              averageLength: response.conversationLength.averageLength ?? null,
              distribution: response.conversationLength.distribution || [],
              totalMessages: response.conversationLength.totalMessages ?? null,
              averageDuration:
                response.conversationLength.averageDuration ?? null,
            }
          : undefined,

        sourceClicks: response.sourceClicks
          ? {
              totalSources: response.sourceClicks.totalSources ?? 0,
              clickedSources: response.sourceClicks.clickedSources ?? 0,
              clickedPercentage: response.sourceClicks.clickedPercentage ?? 0,
              perType: (response.sourceClicks.perType || []).map((t) => ({
                type: t.type,
                total: t.total,
                clicked: t.clicked,
                percentage: t.percentage,
                percentageClickedSources: t.percentageClickedSources ?? 0,
              })),
              avgClicksPerConversation:
                response.sourceClicks.avgClicksPerConversation ?? 0,
            }
          : {
              totalSources: 0,
              clickedSources: 0,
              clickedPercentage: 0,
              perType: [],
              avgClicksPerConversation: 0,
            },
        pageViews: response.pageViews
          ? {
              total: response.pageViews.total ?? 0,
              perPage: response.pageViews.perPage || [],
              perUser: response.pageViews.perUser || [],
              perGroup: response.pageViews.perGroup || [],
              perSession: response.pageViews.perSession || [],
              timeSeries:
                response.pageViews.timeSeries?.map((item) => ({
                  date: item.date,
                  count: item.count,
                })) || [],
            }
          : {
              total: 0,
              perPage: [{ page: null, count: 0 }],
              perSession: [{ sessionId: null, count: 0 }],
              perUser: [{ userId: null, count: 0 }],
              perGroup: [{ group: null, count: 0 }],
              timeSeries: [{ date: null, count: 0 }],
            },
        // Follow-ups data
        followUps: response.followUps
          ? {
              totalGenerated: response.followUps.totalGenerated ?? 0,
              totalClicked: response.followUps.totalClicked ?? 0,
              clickRate: response.followUps.clickRate ?? 0,
              avgRelevanceScore: response.followUps.avgRelevanceScore ?? null,
              aiGenerated: response.followUps.aiGenerated ?? null,
              ruleBased: response.followUps.ruleBased ?? null,
              perCategory: response.followUps.perCategory || [],
              perMethod: response.followUps.perMethod || [],
              timeSeries: response.followUps.timeSeries || [],
              followUpsList: response.followUps.followUpsList || [],
              topPerformers: response.followUps.topPerformers || [],
            }
          : undefined,
      };

      // Update cache with the transformed data and cache key
      this.metricsCache = {
        data: transformedResponse,
        timestamp: now,
        ttl: this.metricsCache.ttl,
        cacheKey,
      };

      return transformedResponse;
    } catch (error) {
      if ((error as any)?.response?.status === 401) {
        throw new Error('Unauthorized');
      }

      // Log the error and throw it to be handled by the component
      logger.error('Failed to fetch all metrics', { error, query });
      throw error;
    }
  }

  // Modified versions of the existing methods to use the consolidated approach
  async getUserActivityMetrics(
    query: MetricsQuery,
    session?: Session | null
  ): Promise<ChartDataPoint[]> {
    try {
      // Ensure we're using valid dates
      const currentDate = new Date();
      const startDate = new Date(currentDate);
      startDate.setDate(startDate.getDate() - 30); // 30 days ago

      // Set time to start of day for startDate and end of day for endDate
      startDate.setHours(0, 0, 0, 0);
      currentDate.setHours(23, 59, 59, 999);

      const formattedQuery = {
        ...query,
        startDate,
        endDate: currentDate,
      };

      const allMetrics = await this.getAllMetrics(formattedQuery, session);
      return allMetrics.userActivity?.dau || [];
    } catch (error) {
      logger.error('Failed to get user activity metrics', { error, query });
      throw error;
    }
  }

  async getTimeDistribution(
    query?: MetricsTimeRange,
    session?: Session | null
  ): Promise<TimeDistributionMetrics> {
    try {
      // Use provided timeRange or default to last 30 days
      const currentDate = new Date();
      const startDate = query?.startDate || new Date(currentDate);
      if (!query?.startDate) {
        startDate.setDate(startDate.getDate() - 30);
      }
      const endDate = query?.endDate || currentDate;

      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);

      const allMetrics = await this.getAllMetrics(
        {
          startDate,
          endDate,
        },
        session
      );

      return (
        allMetrics.timeDistribution || {
          hourly: [],
          daily: [],
          weekly: [],
          monthly: [],
        }
      );
    } catch (error) {
      logger.error('Failed to fetch time distribution metrics', { error });
      throw error;
    }
  }

  async getRetentionMetrics(
    query: MetricsQuery,
    session?: Session | null
  ): Promise<ChartDataPoint[]> {
    try {
      // Ensure we're using valid dates
      const currentDate = new Date();
      const startDate = new Date(currentDate);
      startDate.setDate(startDate.getDate() - 30); // 30 days ago

      // Set time to start of day for startDate and end of day for endDate
      startDate.setHours(0, 0, 0, 0);
      currentDate.setHours(23, 59, 59, 999);

      const formattedQuery = {
        ...query,
        startDate,
        endDate: currentDate,
      };

      const allMetrics = await this.getAllMetrics(formattedQuery, session);
      return allMetrics.retention || [];
    } catch (error) {
      logger.error('Failed to fetch retention metrics', { error, query });
      throw error;
    }
  }

  async getTopicMetrics(
    query: MetricsQuery,
    session?: Session | null
  ): Promise<TopicMetrics> {
    try {
      const currentDate = new Date();
      const startDate = new Date(currentDate);
      startDate.setDate(startDate.getDate() - 30);

      startDate.setHours(0, 0, 0, 0);
      currentDate.setHours(23, 59, 59, 999);

      const allMetrics = await this.getAllMetrics(
        {
          startDate,
          endDate: currentDate,
        },
        session
      );

      // Return empty data instead of throwing if not available
      return (
        allMetrics.topics || {
          topics: [],
          totalQuestions: null,
          averageQuestionsPerUser: null,
        }
      );
    } catch (error) {
      logger.error('Failed to fetch topic metrics', { error });
      throw error;
    }
  }

  async getFeedbackMetrics(
    query?: MetricsTimeRange,
    session?: Session | null
  ): Promise<FeedbackMetrics> {
    try {
      const currentDate = new Date();
      const startDate = query?.startDate || new Date(currentDate);
      if (!query?.startDate) {
        startDate.setDate(startDate.getDate() - 30);
      }
      const endDate = query?.endDate || currentDate;

      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);

      const allMetrics = await this.getAllMetrics(
        {
          startDate,
          endDate,
        },
        session
      );

      return (
        allMetrics.feedback || {
          positive: null,
          negative: null,
          total: null,
          feedbackRate: null,
          topReasons: [],
        }
      );
    } catch (error) {
      logger.error('Failed to fetch feedback metrics', { error });
      throw error;
    }
  }

  async getInteractionMetrics(
    query?: MetricsTimeRange,
    session?: Session | null
  ): Promise<InteractionMetrics> {
    try {
      const currentDate = new Date();
      const startDate = query?.startDate || new Date(currentDate);
      if (!query?.startDate) {
        startDate.setDate(startDate.getDate() - 30);
      }
      const endDate = query?.endDate || currentDate;

      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);

      const allMetrics = await this.getAllMetrics(
        {
          startDate,
          endDate,
        },
        session
      );

      return (
        allMetrics.interactions || {
          totalInteractions: 0,
          averageInteractionsPerUser: 0,
          interactionsByType: [],
        }
      );
    } catch (error) {
      logger.error('Failed to fetch interaction metrics', { error });
      throw error;
    }
  }

  async getDeviceUsage(
    query: MetricsTimeRange,
    session?: Session | null
  ): Promise<PieChartDataPoint[]> {
    try {
      // Ensure we're using valid dates
      const currentDate = new Date();
      const startDate = new Date(currentDate);
      startDate.setDate(startDate.getDate() - 30); // 30 days ago

      // Set time to start of day for startDate and end of day for endDate
      startDate.setHours(0, 0, 0, 0);
      currentDate.setHours(23, 59, 59, 999);

      const formattedQuery = {
        ...query,
        startDate,
        endDate: currentDate,
      };

      const allMetrics = await this.getAllMetrics(formattedQuery, session);
      return allMetrics.deviceUsage || [];
    } catch (error) {
      logger.error('Failed to fetch device usage metrics', { error, query });
      throw error;
    }
  }

  async getBrowserDistribution(
    query: MetricsTimeRange,
    session?: Session | null
  ): Promise<PieChartDataPoint[]> {
    try {
      // Ensure we're using valid dates
      const currentDate = new Date();
      const startDate = new Date(currentDate);
      startDate.setDate(startDate.getDate() - 30); // 30 days ago

      // Set time to start of day for startDate and end of day for endDate
      startDate.setHours(0, 0, 0, 0);
      currentDate.setHours(23, 59, 59, 999);

      const formattedQuery = {
        ...query,
        startDate,
        endDate: currentDate,
      };

      const allMetrics = await this.getAllMetrics(formattedQuery, session);
      return allMetrics.browserDistribution || [];
    } catch (error) {
      logger.error('Failed to fetch browser distribution metrics', {
        error,
        query,
      });
      throw error;
    }
  }

  async getResponseTimeMetrics(
    query?: MetricsTimeRange,
    session?: Session | null
  ): Promise<{
    averageLambdaResponseTime: number | null;
    medianLambdaResponseTime: number | null;
    p95LambdaResponseTime: number | null;
    averageFrontendResponseTime: number | null;
    medianFrontendResponseTime: number | null;
    p95FrontendResponseTime: number | null;
  }> {
    try {
      const currentDate = new Date();
      const startDate = query?.startDate || new Date(currentDate);
      if (!query?.startDate) {
        startDate.setDate(startDate.getDate() - 30);
      }
      const endDate = query?.endDate || currentDate;

      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);

      const allMetrics = await this.getAllMetrics(
        {
          startDate,
          endDate,
        },
        session
      );

      return (
        allMetrics.responseTimes || {
          averageLambdaResponseTime: null,
          medianLambdaResponseTime: null,
          p95LambdaResponseTime: null,
          averageFrontendResponseTime: null,
          medianFrontendResponseTime: null,
          p95FrontendResponseTime: null,
        }
      );
    } catch (error) {
      logger.error('Failed to fetch response time metrics', { error });
      throw error;
    }
  }

  async getPageViewMetrics(
    query?: MetricsTimeRange,
    session?: Session | null
  ): Promise<{
    total: number;
    perPage: Array<{ page: string; count: number }>;
    perUser: Array<{ userId: string; count: number }>;
    perSession: Array<{ sessionId: string; count: number }>;
    perGroup: Array<{ group: string; count: number }>;
    timeSeries: Array<{ x: string; y: number }>;
  }> {
    try {
      const currentDate = new Date();

      // Determine date range
      const startDate = query?.startDate || new Date(currentDate);
      if (!query?.startDate) {
        startDate.setDate(startDate.getDate() - 30);
      }
      const endDate = query?.endDate || currentDate;

      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);

      // --- Fetch everything ---
      const allMetrics = await this.getAllMetrics(
        { startDate, endDate },
        session
      );

      const pageViews = allMetrics.pageViews || {
        perPage: [],
        perUser: [],
        perSession: [],
        perGroup: [],
        timeSeries: [],
        total: 0,
      };

      logger.info('page views:', allMetrics.pageViews);

      // Normalize: perPage
      const perPage = (pageViews.perPage ?? []).map((row: any) => ({
        page: row.page ?? null,
        count: typeof row.count === 'number' ? row.count : 0,
      }));

      // Normalize: perPage
      const perGroup = (pageViews.perGroup ?? []).map((row: any) => ({
        group: row.group ?? null,
        count: typeof row.count === 'number' ? row.count : 0,
      }));

      // Normalize: perUser
      const perUser = (pageViews.perUser ?? []).map((row: any) => ({
        userId: row.userId ?? null,
        count: typeof row.count === 'number' ? row.count : 0,
      }));

      // Normalize: perSession
      const perSession = (pageViews.perSession ?? []).map((row: any) => ({
        sessionId: row.sessionId ?? null,
        count: typeof row.count === 'number' ? row.count : 0,
      }));

      // Normalize: timeSeries → ALWAYS return { x, y }
      const timeSeries = (pageViews.timeSeries ?? []).map((pt: any) => ({
        x:
          typeof pt.date === 'string'
            ? pt.date
            : pt.date instanceof Date
              ? pt.date.toISOString()
              : (pt.x ?? null),
        y:
          typeof pt.count === 'number'
            ? pt.count
            : typeof pt.y === 'number'
              ? pt.y
              : 0,
      }));

      return {
        total: typeof pageViews.total === 'number' ? pageViews.total : 0,
        perPage,
        perUser,
        perSession,
        perGroup,
        timeSeries,
      };
    } catch (error) {
      logger.error('Failed to fetch page view metrics', { error });
      throw error;
    }
  }

  async getConversationLengthMetrics(
    query?: MetricsTimeRange,
    session?: Session | null
  ): Promise<{
    averageLength: number | null;
    distribution: ChartDataPoint[];
    totalMessages: number | null;
    averageDuration?: number | null;
  }> {
    try {
      const currentDate = new Date();
      const startDate = query?.startDate || new Date(currentDate);
      if (!query?.startDate) {
        startDate.setDate(startDate.getDate() - 30);
      }
      const endDate = query?.endDate || currentDate;

      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);

      const allMetrics = await this.getAllMetrics(
        {
          startDate,
          endDate,
        },
        session
      );

      return (
        allMetrics.conversationLength || {
          averageLength: null,
          distribution: [],
          totalMessages: null,
          averageDuration: null,
        }
      );
    } catch (error) {
      logger.error('Failed to fetch conversation length metrics', { error });
      throw error;
    }
  }

  async getSourceClickMetrics(
    query?: MetricsTimeRange,
    session?: Session | null
  ): Promise<{
    totalSources: number | null;
    clickedSources: number | null;
    clickedPercentage: number | null;
    perType: Array<{
      type: string;
      total: number;
      clicked: number;
      percentage: number;
    }>;
    avgClicksPerConversation: number | null;
  }> {
    try {
      const currentDate = new Date();
      const startDate = query?.startDate || new Date(currentDate);
      if (!query?.startDate) {
        startDate.setDate(startDate.getDate() - 30); // default last 30 days
      }
      const endDate = query?.endDate || currentDate;

      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);

      // Reuse consolidated endpoint (preferred) if your backend bundles metrics
      const allMetrics = await this.getAllMetrics(
        {
          startDate,
          endDate,
        },
        session
      );

      return (
        allMetrics.sourceClicks || {
          totalSources: null,
          clickedSources: null,
          clickedPercentage: null,
          perType: [],
          avgClicksPerConversation: null,
        }
      );
    } catch (error) {
      logger.error('Failed to fetch source click metrics', { error, query });
      throw error;
    }
  }

  async getFollowUpMetrics(
    query?: MetricsTimeRange,
    session?: Session | null
  ): Promise<{
    totalGenerated: number | null;
    totalClicked: number | null;
    clickRate: number | null;
    perCategory: Array<{
      category: string;
      generated: number;
      clicked: number;
      rate: number;
    }>;
    perMethod: Array<{
      method: string;
      generated: number;
      clicked: number;
      rate: number;
    }>;
    timeSeries: Array<{
      date: string;
      generated: number;
      clicked: number;
    }>;
    followUpsList: Array<{
      adviceItemId: string;
      originalQuestion: string;
      createdAt: string;
      followUps: Array<{
        id: string;
        question: string;
        category: string;
        order: number;
        isClicked: boolean;
        clickedAt: string | null;
        generationMethod: string;
        relevanceScore: number;
      }>;
    }>;
    topPerformers: Array<{
      id: string;
      question: string;
      category: string;
      generationMethod: string;
      relevanceScore: number;
      clickedAt: string | null;
    }>;
    avgRelevanceScore: number | null;
    aiGenerated: number | null;
    ruleBased: number | null;
  }> {
    try {
      const currentDate = new Date();
      // Create copies of dates to avoid mutating the original objects
      const startDate = query?.startDate ? new Date(query.startDate) : new Date(currentDate);
      if (!query?.startDate) {
        startDate.setDate(startDate.getDate() - 30);
      }
      const endDate = query?.endDate ? new Date(query.endDate) : new Date(currentDate);

      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);

      logger.info('[getFollowUpMetrics] Calling getAllMetrics with dates:', {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      });

      const allMetrics = await this.getAllMetrics(
        {
          startDate,
          endDate,
        },
        session
      );

      logger.info('[getFollowUpMetrics] allMetrics.followUps:', {
        hasFollowUps: !!allMetrics.followUps,
        totalGenerated: allMetrics.followUps?.totalGenerated,
        followUpsListLength: allMetrics.followUps?.followUpsList?.length,
      });

      return (
        allMetrics.followUps || {
          totalGenerated: null,
          totalClicked: null,
          clickRate: null,
          perCategory: [],
          perMethod: [],
          timeSeries: [],
          followUpsList: [],
          topPerformers: [],
          avgRelevanceScore: null,
          aiGenerated: null,
          ruleBased: null,
        }
      );
    } catch (error) {
      logger.error('Failed to fetch follow-up metrics', { error, query });
      throw error;
    }
  }

  // Helper methods for formatting and processing
  private formatQueryParams(
    query: Partial<MetricsQuery>
  ): Record<string, string> {
    const params: Record<string, string> = {};

    if (query.startDate) {
      params.startDate = query.startDate.toISOString();
    }

    if (query.endDate) {
      params.endDate = query.endDate.toISOString();
    }

    if (query.interval) {
      // Adjust interval based on date range
      const diffInDays =
        query.endDate && query.startDate
          ? Math.ceil(
              (query.endDate.getTime() - query.startDate.getTime()) /
                (1000 * 60 * 60 * 24)
            )
          : 0;

      if (diffInDays > 365) {
        params.interval = 'month';
      } else if (diffInDays > 90) {
        params.interval = 'week';
      } else if (diffInDays > 7) {
        params.interval = 'day';
      } else {
        params.interval = 'hour';
      }
    }

    if (query.dimensions) {
      for (const [key, value] of Object.entries(query.dimensions)) {
        params[`dimension_${key}`] = String(value);
      }
    }

    return params;
  }

  private processMetricsResponse(response: MetricsResponse): ChartDataPoint[] {
    return response.data.map((item) => ({
      x: new Date(item.timestamp).getTime(),
      y: item.value,
    }));
  }

  private aggregateDeviceData(
    data: MetricsResponse['data']
  ): PieChartDataPoint[] {
    const deviceCounts = new Map<string, number>();

    for (const item of data) {
      const device = (item.dimensions?.device as string) || 'Unknown';
      deviceCounts.set(device, (deviceCounts.get(device) || 0) + item.value);
    }

    return Array.from(deviceCounts.entries()).map(([name, value]) => ({
      name,
      value,
    }));
  }

  private aggregateBrowserData(
    data: MetricsResponse['data']
  ): PieChartDataPoint[] {
    const browserCounts = new Map<string, number>();

    for (const item of data) {
      const browser = (item.dimensions?.browser as string) || 'Unknown';
      browserCounts.set(
        browser,
        (browserCounts.get(browser) || 0) + item.value
      );
    }

    return Array.from(browserCounts.entries()).map(([name, value]) => ({
      name,
      value,
    }));
  }

  /**
   * Sends the current event queue to the backend.
   */
  private async _sendBatch(): Promise<void> {
    if (this.eventQueue.length === 0) {
      return; // Nothing to send
    }

    // Use the stored session
    const sessionToSendWith = this.currentSession;
    if (!sessionToSendWith?.user?.accessToken) {
      logger.debug('Cannot send metrics batch - no valid session available');
      // Optionally clear queue or retry later? For now, we log and potentially lose events.
      // this.eventQueue = []; // Uncomment to discard events if no session
      this._clearTimer(); // Stop trying if session is invalid
      return;
    }

    const batchToSend = [...this.eventQueue];
    this.eventQueue = []; // Clear queue immediately
    this._clearTimer(); // Clear timer as we are sending now

    try {
      logger.debug(`Sending metrics batch of size ${batchToSend.length}`);
      // Ensure the correct batch endpoint is configured and used
      const batchEndpoint =
        API_CONFIG.ENDPOINTS?.TRACKING?.ACTIVITY || '/metrics/events'; // Use new batch endpoint

      await apiService.post(
        batchEndpoint,
        batchToSend, // Send the array
        sessionToSendWith
      );
      logger.debug('Successfully sent metrics batch');
    } catch (error: any) {
      logger.error('Failed to send metrics batch', {
        error: error?.message || error,
        batchSize: batchToSend.length,
      });
      // Optional: Add events back to queue for retry?
      // this.eventQueue.unshift(...batchToSend);
    }
  }

  /**
   * Clears the batch sending timer.
   */
  private _clearTimer(): void {
    if (this.batchTimerId) {
      clearInterval(this.batchTimerId);
      this.batchTimerId = null;
    }
  }

  /**
   * Starts the batch sending timer if not already running.
   */
  private _startTimer(): void {
    if (!this.batchTimerId) {
      this.batchTimerId = setInterval(() => {
        this._sendBatch();
      }, BATCH_INTERVAL_MS);
    }
  }

  /**
   * Track a metrics event - queues it for batch sending.
   */
  async trackEvent(
    eventData: {
      category: string;
      action: string;
      label?: string;
      value?: number;
      userId?: string;
      sessionId?: string;
      dimensions?: Record<string, any>;
      [key: string]: any;
    }
    // Session is now managed internally via setSession
    // session?: Session | null
  ): Promise<void> {
    if (DISABLE_ALL_METRICS) {
      return;
    }

    // Use the internally stored session
    logger.info('metrics tracking event:', eventData);

    const eventWithTimestamp: TrackedEventData = {
      ...eventData,
      timestamp: new Date().toISOString(),
    };

    this.eventQueue.push(eventWithTimestamp);
    logger.debug(
      `Queued metrics event. Queue size: ${this.eventQueue.length}`,
      {
        category: eventData.category,
        action: eventData.action,
      }
    );

    // Send immediately if batch size reached
    if (this.eventQueue.length >= BATCH_SIZE_LIMIT) {
      this._sendBatch();
    } else {
      // Otherwise, ensure the timer is running
      this._startTimer();
    }
  }

  /**
   * Public method to manually trigger sending the current event queue.
   */
  public flushQueue(): void {
    this._sendBatch();
  }
}

export const metricsService = new MetricsService();
// Ensure session is updated somewhere in the app, e.g., in a global provider
// Example: useEffect(() => { metricsService.setSession(session); }, [session]);
